import { useMemo, useState } from "react";

import { useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { CircularLoader, EmptyState, TabList, TabPanel, Tabs } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import GainAccessBlock from "app/pages/sales-intelligence/common-components/GainAccessBlock/GainAccessBlock";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import EnrichmentQuota from "app/pages/sales-intelligence/modules/enrichment/components/EnrichmentQuota/EnrichmentQuota";
import { EnrichmentTypeDropdown } from "app/pages/sales-intelligence/modules/enrichment/components/EnrichmentTypeDropdown/EnrichmentTypeDropdown";
import { UploadContactsEnrichmentModal } from "app/pages/sales-intelligence/modules/enrichment/components/UploadEnrichmentListModal/UploadContactsEnrichmentModal";
import { UploadEnrichmentListModal } from "app/pages/sales-intelligence/modules/enrichment/components/UploadEnrichmentListModal/UploadEnrichmentListModal";
import { TABS_TRACKING_LIST } from "app/pages/sales-intelligence/modules/enrichment/constants/constants";
import CrmEnrichmentContainer from "app/pages/sales-intelligence/modules/enrichment/containers/CrmEnrichmentContainer/CrmEnrichmentContainer";
import EnrichmentHistoryContainer from "app/pages/sales-intelligence/modules/enrichment/containers/EnrichmentHistoryContainer/EnrichmentHistoryContainer";
import {
    StyledEnrichmentHistoryHeader,
    StyledEnrichmentHistoryTitle,
} from "app/pages/sales-intelligence/modules/enrichment/containers/EnrichmentHistoryContainer/styles";
import {
    StyledEnrichmentEmptyStateContainer,
    StyledEnrichmentHistoryContainer,
    StyledEnrichmentQuotaAndUpload,
    StyledEnrichmentTabsContainer,
    StyledLoaderWrapper,
    StyledTab,
} from "app/pages/sales-intelligence/modules/enrichment/containers/EnrichmentTabsContainer/styles";
import useUploadCSVEnrichmentFile from "app/pages/sales-intelligence/modules/enrichment/hooks/useUploadCSVEnrichmentFile";
import useEnrichmentList from "app/pages/sales-intelligence/modules/enrichment/queries/useEnrichmentList";
import { selectIsEnriching } from "app/pages/sales-intelligence/modules/enrichment/store/selectors/selectors";
import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import { ENRICHMENT_TABS } from "app/pages/sales-intelligence/pages/enrichment/constants/constants";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import {
    selectExcelQuota,
    selectLegacyWorkspacesFetching,
} from "app/pages/sales-intelligence/sub-modules/common/store/selectors";
import { selectRemainingQuota } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";
import { handleGainAccessRedirect } from "app/common/utils/handleGainAccessRedirect";

const options = {
    svg: {
        stroke: colorsPalettes.midnight["50"],
        strokeWidth: "4",
        r: 30,
        cx: "50%",
        cy: "50%",
    },
    style: {
        width: 108,
        height: 108,
    },
};

const EnrichmentTabsContainer = () => {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
    const [enrichmentType, setEnrichmentType] = useState<EnrichmentType>("company");
    const { file, setFile, handleFileChange } = useUploadCSVEnrichmentFile();
    const isWorkspacesFetching = useSelector(selectLegacyWorkspacesFetching);
    const { remaining: remainingQuota } = useSelector(selectExcelQuota);
    const contactsRemainingQuota = useSelector(selectRemainingQuota);
    const isEnriching = useSelector(selectIsEnriching);
    const isEnabledEnrichment = salesSettingsHelper.isCsvEnrichmentEnabled();
    const isQuotaEnrichmentContainer = salesSettingsHelper.isDataCreditsEnabled;

    const { data, isLoading, hasLoadingItems } = useEnrichmentList();

    const getEmptyView = () => {
        if (isWorkspacesFetching || isLoading) {
            return (
                <StyledLoaderWrapper>
                    <CircularLoader options={options} />
                </StyledLoaderWrapper>
            );
        }

        if (!isWorkspacesFetching && !isEnabledEnrichment) {
            return (
                <GainAccessBlock
                    title="si.enrichment.hook.get-access.title"
                    subTitle="si.enrichment.hook.get-access.subtitle"
                    isExploreOurPackages
                    handleGainAccessRedirect={() =>
                        handleGainAccessRedirect(
                            "go to package page",
                            "click",
                            "Internal Link",
                            "Enrichment",
                            "",
                            "Enrichment",
                        )
                    }
                />
            );
        }

        return (
            <StyledEnrichmentEmptyStateContainer
                padding="44px 0"
                margin="32px auto"
                width="360px"
            >
                <EmptyState
                    title={translate("si.section.enrichment.empty.state.title")}
                    subtitle={translate("si.section.enrichment.empty.state.text")}
                />
            </StyledEnrichmentEmptyStateContainer>
        );
    };

    const handleSelectTab = (selectedIndex: number): void => {
        void enrichmentTrackingService.trackEnrichmentAction(TABS_TRACKING_LIST[selectedIndex]);
        setSelectedIndex(selectedIndex);
    };

    const handleDDClick = ({ id }: { id: EnrichmentType }) => {
        setIsCsvModalOpen(true);
        setEnrichmentType(id);
        enrichmentTrackingService.csv.trackTypeDDClick(id);
    };

    const ModalComponentByType = useMemo(
        () => (enrichmentType === "company" ? UploadEnrichmentListModal : UploadContactsEnrichmentModal),
        [enrichmentType],
    );

    return (
        <StyledEnrichmentTabsContainer>
            <Tabs
                selectedIndex={selectedIndex}
                onSelect={handleSelectTab}
            >
                <TabList>
                    {Object.values(ENRICHMENT_TABS).map(({ id, title }) => {
                        return <StyledTab key={id}>{translate(title)}</StyledTab>;
                    })}
                </TabList>

                <TabPanel>
                    <>
                        <StyledEnrichmentHistoryHeader>
                            <I18n
                                data-automation="enrichment-quota-title"
                                element={StyledEnrichmentHistoryTitle}
                                translatable={{
                                    key: "si.enrichment.header.title",
                                }}
                            />
                            <StyledEnrichmentQuotaAndUpload>
                                {isQuotaEnrichmentContainer ? null : (
                                    <EnrichmentQuota
                                        remainingWebsitesQuota={remainingQuota}
                                        remainingContactsQuota={contactsRemainingQuota}
                                    />
                                )}

                                <EnrichmentTypeDropdown
                                    onClick={handleDDClick}
                                    isDisabled={hasLoadingItems || isEnriching || !isEnabledEnrichment}
                                />
                            </StyledEnrichmentQuotaAndUpload>
                        </StyledEnrichmentHistoryHeader>
                        {data?.length && data?.length > 0 ? (
                            <StyledEnrichmentHistoryContainer>
                                <EnrichmentHistoryContainer />
                            </StyledEnrichmentHistoryContainer>
                        ) : (
                            getEmptyView()
                        )}

                        <ModalComponentByType
                            isOpen={isCsvModalOpen}
                            onOpenStateChange={setIsCsvModalOpen}
                            file={file}
                            setFile={setFile}
                            onFileChange={handleFileChange}
                        />
                    </>
                </TabPanel>
                <TabPanel>
                    <CrmEnrichmentContainer
                        onGainAccessRedirect={() =>
                            handleGainAccessRedirect(
                                "go to package page",
                                "click",
                                "Internal Link",
                                "Enrichment",
                                "",
                                "Enrichment",
                            )
                        }
                    />
                </TabPanel>
            </Tabs>
        </StyledEnrichmentTabsContainer>
    );
};

export default EnrichmentTabsContainer;
